import * as D from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Button } from "./button";
import { cn } from "./cn";
import { FormError } from "./feedback";
import { Input } from "./form";

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = "md",
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
}) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-40 bg-[#0b0820]/50 backdrop-blur-sm" />
        <D.Content
          className={cn(
            "fixed top-1/2 left-1/2 z-50 flex max-h-[90vh] w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-lg",
            size === "sm" ? "max-w-md" : size === "lg" ? "max-w-3xl" : "max-w-xl",
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div>
              <D.Title className="text-[16px] font-semibold tracking-tight">{title}</D.Title>
              {description ? <D.Description className="mt-0.5 text-[13px] text-muted">{description}</D.Description> : <D.Description className="sr-only">{String(title)}</D.Description>}
            </div>
            <D.Close className="rounded-lg p-1.5 text-muted hover:bg-surface-2 hover:text-fg" aria-label="Close">
              <X size={16} />
            </D.Close>
          </div>
          {children && <div className="overflow-y-auto px-5 py-4">{children}</div>}
          {footer && <div className="flex justify-end gap-2 border-t border-line bg-surface-2/50 px-5 py-3">{footer}</div>}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

/**
 * Confirmation for dangerous actions. With `confirmText`, the user must type
 * the resource name before the action is enabled.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  tone = "danger",
  confirmText,
  requireReason,
  loading,
  error,
  onConfirm,
  children,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  tone?: "danger" | "primary";
  confirmText?: string;
  requireReason?: boolean;
  loading?: boolean;
  error?: unknown;
  onConfirm(reason: string): void;
  children?: ReactNode;
}) {
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const ready = (!confirmText || typed === confirmText) && (!requireReason || reason.trim().length >= 3);
  const close = (o: boolean) => {
    if (!o) {
      setTyped("");
      setReason("");
    }
    onOpenChange(o);
  };
  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => close(false)} disabled={loading}>
            Cancel
          </Button>
          <Button variant={tone === "danger" ? "danger" : "primary"} disabled={!ready} loading={loading} onClick={() => onConfirm(reason.trim())}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {children}
        {requireReason && (
          <label className="flex flex-col gap-1.5 text-[13px]">
            <span className="font-medium">Reason (recorded in the audit log)</span>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Requested by customer via ticket #1234" />
          </label>
        )}
        {confirmText && (
          <label className="flex flex-col gap-1.5 text-[13px]">
            <span>
              Type <span className="font-mono font-semibold">{confirmText}</span> to confirm
            </span>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} />
          </label>
        )}
        <FormError error={error} />
      </div>
    </Dialog>
  );
}
